import { CustomerType, RelationshipType } from '@common/entities';
import type { LeadProfileApplicant } from 'state/slices/leadProfileSlice';

export const getRelationshipLabel = (
  relationshipType: string,
  isNonResponsibleOccupant?: boolean,
  customerRelationshipId?: number,
  customerRelationshipsData?: any[],
  marketingName?: string,
  preferMarketingName: boolean = false,
) => {
  // Try to use the dynamic frontend list first in case the user just changed the type without reloading
  if (customerRelationshipId && customerRelationshipsData) {
    const relationship = customerRelationshipsData.find(
      (r: any) => Number(r.id) === Number(customerRelationshipId),
    );
    if (relationship) {
      return preferMarketingName
        ? relationship.marketing_name || relationship.name
        : relationship.name || relationship.marketing_name;
    }
  }

  // Fallback to the prepopulated marketingName from the backend response
  if (marketingName) {
    return marketingName;
  }

  // Final fallback to the hardcoded default mapping
  switch (relationshipType) {
    case RelationshipType.Primary:
      return 'Primary';
    case RelationshipType.Coapplicant:
      return 'Co-Applicant';
    case RelationshipType.Guarantor:
      return 'Guarantor';
    case RelationshipType.Other:
      return isNonResponsibleOccupant ? 'Not Responsible' : 'Non-Leasing Occupant';
    default:
      return 'Other';
  }
};

/**
 * Returns true for customer types that do not require contact information (email/phone).
 * Both NotResponsible (children) and NonLeasingOccupant (non-signing occupants) share
 * this characteristic: they live in the unit but do not sign the lease and are not
 * expected to have their own contact details on the application.
 */
export const isNonResponsibleCustomerType = (customerTypeId?: string | null): boolean =>
  customerTypeId === CustomerType.NotResponsible ||
  customerTypeId === CustomerType.NonLeasingOccupant;

/**
 * Non-Leasing Occupant (customer_type_id 5). Per Entrata monolith rules this
 * applicant type is treated as an adult (18+) and requires a phone number, but
 * email is optional. This differs from NotResponsible children (who are minors
 * with no contact details), so it needs its own check rather than being grouped
 * under isNonResponsibleCustomerType for field-requirement decisions.
 */
export const isNonLeasingOccupantCustomerType = (customerTypeId?: string | null): boolean =>
  customerTypeId === CustomerType.NonLeasingOccupant;

/**
 * Adult check by customer type: only NotResponsible (children) are minors; every other type
 * (including NonLeasingOccupant adults) is an adult. Drives birth-date min/max validation.
 */
export const isAdultCustomerType = (customerTypeId?: string | null): boolean =>
  customerTypeId !== CustomerType.NotResponsible;

export const getRequiresContactInfo = (
  customerRelationshipId?: string | number | null,
  dataToFilter?: any[],
  isAdult: boolean = true,
) => {
  if (!isAdult) return false;
  if (!customerRelationshipId || !dataToFilter) return true;

  const relationship = dataToFilter.find(
    (r: any) => Number(r.id) === Number(customerRelationshipId),
  );

  if (relationship) {
    return !isNonResponsibleCustomerType(relationship.customer_type_id?.toString());
  }

  return true;
};

/**
 * Display order for applicant tabs: Primary → Guarantors → Co-applicants → Occupants.
 * Unknown types are pushed to the end. Within the same type, original insertion order is preserved.
 */
const RELATIONSHIP_TYPE_TAB_ORDER: Partial<Record<string, number>> = {
  [RelationshipType.Primary]: 0,
  [RelationshipType.Guarantor]: 1,
  [RelationshipType.Coapplicant]: 2,
  [RelationshipType.Other]: 3,
};

export const sortApplicants = (applicants: LeadProfileApplicant[]): LeadProfileApplicant[] =>
  [...applicants].sort(
    (a, b) =>
      (RELATIONSHIP_TYPE_TAB_ORDER[a.relationshipType] ?? 99) -
      (RELATIONSHIP_TYPE_TAB_ORDER[b.relationshipType] ?? 99),
  );

export const getIsNonResponsibleOccupant = (
  responseId: string,
  relationshipType: string,
  email?: string,
  entrataCustomerRelationshipId?: number,
  dataToFilter?: any[],
  coApplicantList: any[] = [],
) => {
  const coApp = coApplicantList.find((c: any) => c.responseId === responseId);
  const relationshipId =
    entrataCustomerRelationshipId || (coApp?.relationship ? Number(coApp.relationship) : undefined);

  if (relationshipId && dataToFilter) {
    const relationship = dataToFilter.find((r: any) => Number(r.id) === Number(relationshipId));
    if (relationship) {
      return (
        relationship.customer_type_id?.toString() === CustomerType.NotResponsible ||
        relationship.customer_type_id?.toString() === CustomerType.NonLeasingOccupant
      );
    }
  }

  return (
    coApp?.coapplicant_age_over_17 === 'no' ||
    (!coApp && relationshipType === RelationshipType.Other && !email)
  );
};

/**
 * Resolves the Entrata customer type for a Lead Profile applicant. Visibility rules
 * consume this value directly so they do not have to infer an applicant's needs from
 * relationship type, email, or a combined "non-responsible" flag.
 */
export const getApplicantCustomerType = (
  responseId: string,
  entrataCustomerRelationshipId?: number,
  dataToFilter?: any[],
  coApplicantList: any[] = [],
): CustomerType | undefined => {
  const coApp = coApplicantList.find((c: any) => c.responseId === responseId);
  const relationshipId =
    entrataCustomerRelationshipId || (coApp?.relationship ? Number(coApp.relationship) : undefined);

  if (!relationshipId || !dataToFilter) {
    return coApp?.coapplicant_age_over_17 === 'no' ? CustomerType.NotResponsible : undefined;
  }

  const customerTypeId = dataToFilter.find(
    (relationship: any) => Number(relationship.id) === Number(relationshipId),
  )?.customer_type_id;

  if (Object.values(CustomerType).includes(customerTypeId?.toString() as CustomerType)) {
    return customerTypeId.toString() as CustomerType;
  }

  return coApp?.coapplicant_age_over_17 === 'no' ? CustomerType.NotResponsible : undefined;
};

export function getApplicantDisplayName(
  applicants: LeadProfileApplicant[],
  responseId?: string,
): string | undefined {
  if (!responseId) {
    return undefined;
  }

  const applicant = applicants.find((entry) => entry.responseId === responseId);
  if (!applicant) {
    return undefined;
  }

  const name = [applicant.firstName, applicant.lastName].filter(Boolean).join(' ').trim();
  return name || undefined;
}

const COMPANY_USER_NAME_QUERY_PARAMS = ['user_name', 'company_user_name'] as const;

export function resolveCompanyUserDisplayName(
  searchParams: Pick<URLSearchParams, 'get'>,
): string | undefined {
  const matchedParam = COMPANY_USER_NAME_QUERY_PARAMS.map((paramName) =>
    searchParams.get(paramName)?.trim(),
  ).find(Boolean);

  return matchedParam || undefined;
}
